import * as S from "../styles";
import {
  walking,
  driving,
  minute,
  hour,
  yards,
  miles,
  meters,
  kilometers,
  fastestRoute,
  noRoute,
  trySuggestion,
  calculateCommute,
  searchMap,
} from "../translations";
import { TrackingEvents, ProductNames } from "@local-logic/core/tracking";
import {
  Select,
  Spacer,
  SVG,
  Text,
  LoadingItem,
  theme,
} from "@local-logic/core/ui";
import { primitiveUtils, mapUtils } from "@local-logic/core/utils";
import React, { useEffect, useMemo, useState } from "react";
import DrivingSVG from "~/assets/icons/Driving.svg";
import WalkingSVG from "~/assets/icons/Walking.svg";
import CloseSVG from "~/assets/icons/close.svg";
import AnimateHeight from "~/components/AnimateHeight";
import Search from "~/components/Search";
import { useStateManagerContext } from "~/context/stateManager";
import { useTracking } from "~/context/trackingProvider";
import { useCalculateRoute } from "~/features/calculateRoute/useCommute";
import useIsMobile from "~/hooks/useIsMobile";
import { useLocalContent } from "~/sdks/LocalContent/context";
import { LocalContentProps } from "~/sdks/LocalContent/types";

export interface CommuteCalculatorProps {
  className?: string;
  onClose: () => void;
}

const MapGLCommuteCalculator = ({
  className,
  onClose,
}: CommuteCalculatorProps) => {
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const { setCommuteGeojson, destination, setDestination } = useLocalContent();
  const [transportMode, setTransportMode] =
    useState<keyof typeof transportModeMap>("pedestrian");
  const isMobile = useIsMobile();
  const tracking = useTracking();

  const { data, isFetching, isFetched } = useCalculateRoute({
    destination,
    transportMode,
    origin: `${sdkState?.options?.marker?.lat},${sdkState?.options?.marker?.lng}`,
    isOpen: true,
  });

  useEffect(() => {
    if (!data?.summary) {
      return;
    }

    setCommuteGeojson(mapUtils.decodePolyline(data.polyline));
  }, [data]);

  const transportModeMap = {
    pedestrian: [walking, WalkingSVG],
    car: [driving, DrivingSVG],
  };

  const duration = useMemo(() => {
    if (!data?.summary) {
      return null;
    }
    const hours = Math.floor(data.summary.typicalDuration / 60 / 60);
    const minutes = Math.floor((data.summary.typicalDuration / 60) % 60);

    if (hours === 0) {
      return `${minutes} ${primitiveUtils.string.pluralize(minute, minutes)}`;
    }

    return `${hours} ${primitiveUtils.string.pluralize(
      hour,
      hours
    )} ${minutes} ${primitiveUtils.string.pluralize(minute, minutes)}`;
  }, [data]);

  const distance = useMemo(() => {
    if (!data?.summary) {
      return null;
    }

    // HereApi returns the length in metric
    let distanceInUnit = data.summary.length;
    const isImperialSystem = sdkState?.options?.distanceUnit === "imperial";

    if (isImperialSystem) {
      distanceInUnit *= 1.0936132983;
    }

    const kilometersOrMiles = Math.floor(
      distanceInUnit / (isImperialSystem ? 1760 : 1000)
    );

    if (kilometersOrMiles === 0) {
      return `${Math.floor(distanceInUnit)} ${
        isImperialSystem ? yards : meters
      }`;
    }

    return `${kilometersOrMiles} ${isImperialSystem ? miles : kilometers}`;
  }, [data]);

  const getResults = () => {
    if (isFetching) {
      return <LoadingItem height="70.4px" width="100%" />;
    }

    if (data?.summary) {
      return (
        <S.CommuteInfo>
          <Text variant="h5">{duration}</Text>
          <Text>
            {distance} – {fastestRoute}
          </Text>
        </S.CommuteInfo>
      );
    }

    return (
      <S.CommuteInfo>
        <Text variant="h5">{noRoute}</Text>
        <Text>{trySuggestion}</Text>
      </S.CommuteInfo>
    );
  };

  const handleUpdateTransportMode = (mode: keyof typeof transportModeMap) => {
    setTransportMode(mode);

    tracking.postEvent({
      eventType: TrackingEvents.LOCALCONTENT_TRANSPORT_METHOD_CLICKED,
      product: ProductNames.LOCAL_CONTENT,
      isMobile,
      eventAttributes: [
        {
          label: "commute-select-mode",
          data: mode,
        },
      ],
    });
  };

  return (
    <S.CommuteCalculatorContainer className={className}>
      <S.IconButton
        size="medium"
        color="neutral"
        onClick={onClose}
        SVGElement={CloseSVG as unknown as React.FC}
      />

      <S.CommuteSearch>
        <Search
          label={calculateCommute}
          placeholder={destination?.label || searchMap}
          renderListAbove={isMobile}
          renderListAbovePosition={30}
          onSearch={(e) => {
            setDestination({ lat: e.latitude, lng: e.longitude });
          }}
        />
        <S.SelectContainer>
          <Select.Root
            value={transportMode}
            onValueChange={(v: keyof typeof transportModeMap) =>
              handleUpdateTransportMode(v)
            }
          >
            <Select.Trigger>
              <SVG
                SVGElement={
                  transportModeMap[transportMode][1] as unknown as React.FC
                }
                fill={theme["--ll-color-on-background-variant1"]}
                width={16}
              />
              {transportModeMap[transportMode][0]}
              <Select.Content>
                <Select.Item value="car">{driving}</Select.Item>
                <Select.Item value="pedestrian">{walking}</Select.Item>
              </Select.Content>
            </Select.Trigger>
          </Select.Root>
        </S.SelectContainer>
      </S.CommuteSearch>
      {(isFetched || isFetching) && (
        <>
          <Spacer vertical spacing="--ll-spacing-x-small" />
          <AnimateHeight open={!!data}>{getResults()}</AnimateHeight>
        </>
      )}
    </S.CommuteCalculatorContainer>
  );
};

export default MapGLCommuteCalculator;
